import { STATIC_PLANS } from '../data/staticPlans';
import type { PlanConfig } from '../services/subscriptionService';

/** Quick amounts the top-up form offers; amounts under the plan's minimum are left out. */
const QUICK_TOP_UP_AMOUNTS = [50, 100, 200, 500];

/**
 * Smallest credit top-up the plan sells. Checkout refuses less, because a smaller
 * amount would be priced under the card processors' minimum charge.
 */
export const getMinimumTopUpCredits = (plans: Record<string, PlanConfig>, planId: string): number => (
  plans[planId]?.minimum_topup_credits ?? STATIC_PLANS[planId]?.minimum_topup_credits ?? 1
);

/** Three quick amounts, the first at or above the plan's minimum. */
export const getQuickTopUpAmounts = (minimum: number): number[] => {
  const amounts = QUICK_TOP_UP_AMOUNTS.filter((amount) => amount >= minimum);

  return amounts.length >= 3 ? amounts.slice(0, 3) : [minimum, minimum * 2, minimum * 4];
};

/**
 * What checkout charges for a top-up: credits × rate rounded half-up to the cent
 * (or paisa), exactly as the backend rounds it. toFixed(4) first drops the
 * floating-point noise that would otherwise round 7296.5 paise down.
 */
export const topUpCharge = (credits: number, rate: number): number => (
  Math.round(Number((credits * rate * 100).toFixed(4))) / 100
);

/** The API refuses a single top-up above this (it stays inside Stripe's and Razorpay's per-payment limits). */
export const MAX_TOP_UP_CREDITS = 250000;

/**
 * A typed credit amount as the form keeps it: a whole number from 1 to MAX_TOP_UP_CREDITS.
 * Prod 2026-10-10: an unbounded input reached checkout as 1.0080800508808008e+21.
 */
export const clampTopUpCredits = (value: number): number => (
  Number.isFinite(value) ? Math.min(MAX_TOP_UP_CREDITS, Math.max(1, Math.floor(value))) : 1
);
